import { useState } from 'react';

/** Copies a share URL to the clipboard, showing a brief inline confirmation — the same
 * copy-to-clipboard behavior as `ui.tsx`'s `CopyRow`, just icon-sized for a list/grid item.
 * Both `aria-label` (screen readers) and `title` (native hover tooltip for sighted mouse users)
 * are set — the two aren't interchangeable, `aria-label` alone produces no visible tooltip. */
export function ShareIconAction({ shareUrl }: { shareUrl: string }) {
  const [copied, setCopied] = useState(false);
  const label = copied ? 'Copied!' : 'Copy share link';
  return (
    <button
      className="icon-button"
      aria-label={label}
      title={label}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(shareUrl);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          // Clipboard write can fail in some browsers/contexts — nothing more we can do here.
        }
      }}
    >
      {copied ? '✅' : '🔗'}
    </button>
  );
}

/** Move-up / move-down icon buttons for manually reordering a list — the keyboard- and
 * touch-accessible fallback to dragging, not just a visual affordance; both perform the exact
 * same reorder a drag would. */
export function ReorderIconActions({
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}: {
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}) {
  return (
    <>
      <button
        className="icon-button"
        aria-label="Move up"
        title="Move up"
        disabled={!canMoveUp}
        onClick={onMoveUp}
      >
        ▲
      </button>
      <button
        className="icon-button"
        aria-label="Move down"
        title="Move down"
        disabled={!canMoveDown}
        onClick={onMoveDown}
      >
        ▼
      </button>
    </>
  );
}

/** A confirm-then-act icon button — used for quick delete, unshare, and remove-from-playlist,
 * which all share the same "are you sure, then call a mutation" shape already used on the detail
 * pages (e.g. detail-page.tsx's delete button). */
export function ConfirmIconAction({
  icon,
  label,
  confirmMessage,
  onConfirm,
  disabled,
}: {
  icon: string;
  label: string;
  confirmMessage: string;
  onConfirm: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      className="icon-button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={() => {
        if (confirm(confirmMessage)) onConfirm();
      }}
    >
      {icon}
    </button>
  );
}
